"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import {
  Asset,
  Item,
  AssetType,
  ImageView,
  LottieAnimation,
  ActivityIndicator,
  Box,
} from "@thenounproject/lingo-core";
import PlayPauseOverlay from "./elements/PlayPauseOverlay";
import VideoPlayer from "./elements/VideoPlayer";
import useAssetOriginalUrl from "@actions/assets/useOriginalAssetUrl";
import { getAssetPreviewDimensions } from "@helpers/getAssetPreviewDimensions";
import useResponsiveThumbnailSize from "@hooks/useResponsiveThumbnailSize";
import getAssetBackground from "@src/helpers/getAssetBackground";

const StyledVideo = styled.video`
  object-fit: contain;
  height: 100%;
  width: 100%;
  &::-webkit-media-controls {
    display: none !important;
  }
`;

const MotionContainer = styled(Box).attrs({
  width: "100%",
  height: "100%",
})``;

const LottieWrapper = styled.div<{ maxW?: number; maxH?: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 100%;
  height: 100%;
  ${props => props.maxW && `max-width: ${props.maxW}px;`}
  ${props => props.maxH && `max-height: ${props.maxH}px;`}
  cursor: pointer;
  pointer-events: auto;
`;

type Props = {
  asset: Asset;
  item?: Item;
  autoplay?: boolean;
  showControls?: boolean;
  lazy?: boolean;
};

function AssetPreviewMotion({ asset, autoplay = false, showControls = false, lazy = true }: Props) {
  const dims = getAssetPreviewDimensions(asset);
  const { data: url } = useAssetOriginalUrl(asset);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [showPauseButton, setShowPauseButton] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { ref: lazyRef, inView, thumbnailSize } = useResponsiveThumbnailSize();
  const hasPadding = asset.meta.backgroundColor !== "clear";
  const theme = useTheme();
  const backgroundColor = useMemo(() => getAssetBackground(asset, theme), [asset, theme]);

  const isVideo = AssetType.videoTypes.has(asset.type);
  const isGif = asset.type === AssetType.gif;
  const isLottie = asset.type === AssetType.lottie;

  const poster = asset.thumbnails?.[thumbnailSize];

  // Autoplay via IntersectionObserver (video only — GIF/Lottie use state)
  useEffect(() => {
    if (!autoplay || !isVideo || !containerRef.current) return;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsInView(entry.isIntersecting);
          if (entry.isIntersecting && videoRef.current) {
            void videoRef.current.play().catch(() => {
              videoRef.current!.muted = true;
              void videoRef.current!.play();
            });
            setIsPlaying(true);
          } else if (!entry.isIntersecting && videoRef.current) {
            videoRef.current.pause();
            setIsPlaying(false);
          }
        });
      },
      { threshold: 0.5, rootMargin: "50px" }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [autoplay, isVideo, asset.uuid, url]);

  // GIF/Lottie autoplay via state
  useEffect(() => {
    if (isGif || isLottie) setIsPlaying(autoplay);
  }, [autoplay, isGif, isLottie]);

  // Reset playing state when asset changes
  useEffect(() => {
    setIsPlaying(autoplay);
  }, [asset.uuid, autoplay]);

  const handlePlay = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isVideo && videoRef.current) {
        void videoRef.current.play()?.catch(() => {
          if (videoRef.current) {
            videoRef.current.muted = true;
            void videoRef.current.play();
          }
        });
      }
      setShowPauseButton(false);
      setIsPlaying(true);
    },
    [isVideo]
  );

  const handlePause = useCallback(() => {
    if (!autoplay) {
      if (isVideo && videoRef.current) videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [autoplay, isVideo]);

  const handleMouseMove = useCallback(() => {
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    setShowPauseButton(true);
    pauseTimerRef.current = setTimeout(() => setShowPauseButton(false), 3000);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    setShowPauseButton(false);
  }, []);

  useEffect(() => {
    return () => {
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    };
  }, []);

  const pauseMouseProps =
    isPlaying && !autoplay ? { onMouseMove: handleMouseMove, onMouseLeave: handleMouseLeave } : {};

  function renderMotionContent() {
    if (asset.meta.assetProcessing !== "complete") {
      return <ActivityIndicator size="small" color="gray" center />;
    }

    if (isVideo) {
      // Detail mode: thumbnail → click → VideoPlayer with controls
      if (showControls && isPlaying) {
        return <VideoPlayer src={url} placeholder={poster} dimensions={asset.dimensions} />;
      }

      if (!url) return <ImageView width="100%" height="100%" src={poster} />;

      return (
        <MotionContainer ref={containerRef} {...pauseMouseProps}>
          <StyledVideo
            ref={videoRef}
            src={url}
            loop
            muted
            playsInline
            style={{
              maxWidth: asset.width,
              maxHeight: asset.height,
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translateX(-50%) translateY(-50%)",
            }}
            preload="metadata"
            poster={poster}
            onCanPlay={async () => {
              if (autoplay && isInView && videoRef.current) {
                try {
                  await videoRef.current.play();
                  setIsPlaying(true);
                } catch {
                  // autoplay blocked
                }
              }
            }}
            onDragStart={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
          {!isPlaying && <PlayPauseOverlay mode="play" onClick={handlePlay} />}
          {isPlaying && !autoplay && (
            <PlayPauseOverlay mode="pause" visible={showPauseButton} onClick={handlePause} />
          )}
        </MotionContainer>
      );
    }

    if (isLottie) {
      if (!isPlaying) {
        return (
          <>
            <ImageView width="100%" height="100%" src={poster} />
            <PlayPauseOverlay mode="play" onClick={handlePlay} />
          </>
        );
      }
      return (
        <MotionContainer {...pauseMouseProps}>
          {/* Hidden image sizes the container correctly */}
          <ImageView width="100%" height="100%" src={poster} style={{ visibility: "hidden" }} />
          <LottieWrapper maxW={dims?.w} maxH={dims?.h}>
            <LottieAnimation permalink={asset.permalink} />
          </LottieWrapper>
          {!autoplay && (
            <PlayPauseOverlay mode="pause" visible={showPauseButton} onClick={handlePause} />
          )}
        </MotionContainer>
      );
    }

    // GIF
    if (!isPlaying) {
      return (
        <>
          <ImageView width="100%" height="100%" src={poster} />
          <PlayPauseOverlay mode="play" onClick={handlePlay} />
        </>
      );
    }
    return (
      <MotionContainer {...pauseMouseProps}>
        <ImageView width="100%" height="100%" src={asset.permalink} />
        {!autoplay && (
          <PlayPauseOverlay mode="pause" visible={showPauseButton} onClick={handlePause} />
        )}
      </MotionContainer>
    );
  }

  const shouldRender = !lazy || inView;
  return (
    <MotionContainer ref={lazyRef} p={hasPadding ? "8px" : undefined} {...backgroundColor}>
      {shouldRender ? (
        renderMotionContent()
      ) : (
        <ActivityIndicator size="small" color="gray" center />
      )}
    </MotionContainer>
  );
}

export default React.memo(AssetPreviewMotion);
